// Session token written by the landing signup/login modal.
// The login response is `{ access_token, token_type }`. Signup does not
// return a token today; callers fall back to /api/auth/login.
export const TOKEN_KEY = "fsrs_token";

export function accessTokenFrom(data) {
    const token = data && data.access_token;
    return typeof token === "string" && token ? token : null;
}

export function readAuthToken() {
    if (typeof window === "undefined") return null;
    try {
        return localStorage.getItem(TOKEN_KEY) || null;
    } catch {
        return null;
    }
}

export function storeAuthToken(token) {
    if (typeof token !== "string" || !token) return null;
    localStorage.setItem(TOKEN_KEY, token);
    notifyAuthChange();
    return token;
}

export function clearAuthToken() {
    try {
        localStorage.removeItem(TOKEN_KEY);
    } catch {
        // Storage can throw in private mode. The header still re-reads.
    }
    notifyAuthChange();
}

export function isLoggedIn() {
    return Boolean(readAuthToken());
}

// Same-tab listeners. The storage event only fires in other tabs.
export const AUTH_CHANGE_EVENT = "fsrs-auth-change";

function notifyAuthChange() {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
}

// Header display session. Login stores a JWT whose subject is the email
// and whose exp is one week out. The signature is checked by the API;
// the header only needs a readable, unexpired email subject. Opaque test
// tokens and expired tokens are not a display session. isLoggedIn() is
// unchanged: any stored token still counts as logged in for entry routing.
export function readAuthSession() {
    const payload = decodeJwtPayload(readAuthToken());
    if (!payload) return null;
    if (!expiryIsFuture(payload.exp)) return null;
    const email = emailFromSubject(payload.sub);
    if (!email) return null;
    return { email };
}

function expiryIsFuture(exp) {
    return typeof exp === "number" && Number.isFinite(exp) && Date.now() < exp * 1000;
}

function emailFromSubject(sub) {
    if (typeof sub !== "string") return null;
    const email = sub.trim();
    if (!email || email.length > 254 || /\s/.test(email)) return null;
    const at = email.indexOf("@");
    if (at <= 0 || at !== email.lastIndexOf("@") || at === email.length - 1) return null;
    return email;
}

function decodeJwtPayload(token) {
    if (typeof token !== "string") return null;
    const parts = token.split(".");
    if (parts.length !== 3 || !parts[0] || !parts[1] || !parts[2]) return null;
    try {
        const payload = JSON.parse(decodeBase64Url(parts[1]));
        if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
        return payload;
    } catch {
        return null;
    }
}

function decodeBase64Url(segment) {
    const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padLength = (4 - (normalized.length % 4)) % 4;
    const binary = atob(normalized + "=".repeat(padLength));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new TextDecoder().decode(bytes);
}
